import { isDefined } from "@clipboard-health/util-ts";
import { getTokenExpiryInSeconds, isTokenNearExpiry } from "@src/appV2/Auth/cognito/tokenUtils";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";

/**
 * Token expiry threshold in seconds.
 * Tokens will be proactively refreshed when they're within this
 * many seconds of expiring, to avoid edge cases where a token
 * expires between validation and API call completion.
 */
const TOKEN_EXPIRY_THRESHOLD_SECONDS = 60;

/**
 * Shared in-flight refresh promise. Cognito rotates refresh tokens on each
 * refresh call; two concurrent refreshes present the same refresh token and
 * trip `RefreshTokenReuseException`, which invalidates the session. Deduping
 * concurrent callers onto a single request avoids the race.
 */
let inFlightRefresh: Promise<string | undefined> | undefined;

/**
 * Gets a Cognito ID token with proactive refresh to work around an Amplify v6
 * bug where fetchAuthSession() doesn't auto-refresh tokens for
 * CUSTOM_WITHOUT_SRP auth flow.
 *
 * Accesses the Cognito singleton directly so callers don't need to manage
 * the instance themselves.
 *
 * @see https://github.com/aws-amplify/amplify-js/issues/14618
 */
export async function getCognitoIdTokenWithProactiveRefresh(
  forceRefresh?: boolean
): Promise<string | undefined> {
  if (forceRefresh) {
    return await refreshIdTokenSingleFlight();
  }

  const cognitoAuth = getCognitoSingleton();
  const cognitoToken = await cognitoAuth.getIdToken(false);
  if (!cognitoToken) {
    return undefined;
  }

  if (isTokenNearExpiry(cognitoToken, TOKEN_EXPIRY_THRESHOLD_SECONDS)) {
    const expiryInSeconds = getTokenExpiryInSeconds(cognitoToken);
    logEvent(APP_V2_APP_EVENTS.AUTH_TOKEN_PROACTIVE_REFRESH, {
      tokenExpiryInSeconds: expiryInSeconds,
      thresholdSeconds: TOKEN_EXPIRY_THRESHOLD_SECONDS,
    });

    const refreshedToken = await refreshIdTokenSingleFlight();
    if (refreshedToken) {
      return refreshedToken;
    }

    // If refresh fails but the token is still valid, keep using it
    if (expiryInSeconds > 0) {
      return cognitoToken;
    }

    return undefined;
  }

  return cognitoToken;
}

/**
 * Drops any pending refresh promise so the next caller starts a fresh request.
 * Must be called on auth-session boundaries (logout, impersonation start/stop,
 * user switch) so a new user never awaits the previous user's in-flight token.
 */
export function resetInFlightRefresh(): void {
  inFlightRefresh = undefined;
}

async function refreshIdTokenSingleFlight(): Promise<string | undefined> {
  if (isDefined(inFlightRefresh)) {
    return await inFlightRefresh;
  }

  const cognitoAuth = getCognitoSingleton();
  const pending = cognitoAuth.getIdToken(true).finally(() => {
    if (inFlightRefresh === pending) {
      inFlightRefresh = undefined;
    }
  });
  inFlightRefresh = pending;

  return await pending;
}
